const surveySuccessHandler = async (response: Response) => {
  try {
    const contentType = response.headers.get('content-type');

    let data;
    if (contentType && contentType.indexOf('application/json') !== -1) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      // Extract error message from different possible response formats
      let errorMessage = response.status.toString();

      if (typeof data === 'string') {
        // If data is a string (like "You have already completed this survey")
        errorMessage = data;
      } else if (data && typeof data === 'object') {
        // If data is an object, try different possible error message fields
        errorMessage = data.error || data.message || data.detail || response.status.toString();
      }

      // Create error object with status code for better error handling
      const error = new Error(errorMessage);
      (error as any).status = response.status;
      return Promise.reject(error);
    }

    return data;
  } catch (error) {
    return Promise.reject(new Error('No response from server'));
  }
};

const surveyErrorHandler = (error: Error) => {
  throw error;
};

const getSurveyApiClient = ({
  baseURL,
  successHandler,
  errorHandler,
}: {
  baseURL: string;
  successHandler: (response: any) => any;
  errorHandler: (error: Error) => any;
}) => {
  const surveyClient = (url: string, requestOptions?: RequestInit) =>
    fetch(`${surveyClient.baseURL}${url}`, requestOptions)
      .then(successHandler)
      .catch(errorHandler);

  surveyClient.baseURL = baseURL;

  return surveyClient;
};

export const surveyApiClient = getSurveyApiClient({
  baseURL: process.env['NX_MSG_API_HOST'] as string,
  successHandler: surveySuccessHandler,
  errorHandler: surveyErrorHandler,
});

// Survey-specific API methods
export const surveyApi = {
  validateToken: async (surveyId: string, token: string) => {
    return surveyApiClient(`/surveys/${surveyId}/access`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
    });
  },

  fetchSurveyQuestions: async (surveyId: string, token: string) => {
    return surveyApiClient(`/surveys/${surveyId}/form`, {
      method: 'GET',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
    });
  },

  submitSurveyResponse: async (
    surveyId: string,
    token: string,
    responses: Array<{
      questionKey: string;
      answer: string;
    }>
  ) => {
    return surveyApiClient(`/surveys/${surveyId}/responses`, {
      method: 'POST',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ responses }),
    });
  },

  fetchPriorityAreas: async (surveyId: string, token: string) => {
    return surveyApiClient(`/surveys/${surveyId}/priority-areas`, {
      method: 'GET',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
    });
  },

  submitPriorityAreas: async (
    surveyId: string,
    token: string,
    priorityAreas: Array<{
      areaKey: string;
      areaText: string;
      ratingCategory: string;
      priorityRank: number;
      comment: string | null;
    }>
  ) => {
    return surveyApiClient(`/surveys/${surveyId}/priority-areas`, {
      method: 'POST',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ priorityAreas }),
    });
  },
};

export default surveyApiClient;
